"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";

import type {
  GlyphLoginMode,
  GlyphSessionApi,
} from "@/lib/wallet/glyph-account";

const REMEMBERED = "mudwater:glyph";

/** Whether this browser signed in to Glyph before, so Privy loads at once. */
function remembered(): boolean {
  try {
    return localStorage.getItem(REMEMBERED) === "1";
  } catch {
    return false;
  }
}
function remember(on: boolean) {
  try {
    if (on) localStorage.setItem(REMEMBERED, "1");
    else localStorage.removeItem(REMEMBERED);
  } catch {
    // Private mode: Glyph then loads on the next click instead.
  }
}
const noSubscribe = () => () => undefined;
function login(session: GlyphSessionApi, mode: GlyphLoginMode) {
  if (mode === "existing-evm") session.login(mode);
  else session.login();
}

/** Back from Google, X or Apple: Privy must mount to finish the login. */
function oauthReturn(): boolean {
  try {
    return new URLSearchParams(location.search).has("privy_oauth_code");
  } catch {
    return false;
  }
}

export function useGlyphSessionLifecycle() {
  // Capture document startup once on the client, after the server snapshot.
  // Privy removes the OAuth URL before publishing authentication; a live URL
  // snapshot could unmount it between those steps and strand the return.
  const [startupSnapshot] = useState(() => {
    let initial: boolean | undefined;
    return () => (initial ??= remembered() || oauthReturn());
  });
  const wasRemembered = useSyncExternalStore(
    noSubscribe,
    startupSnapshot,
    () => false
  );
  const [wanted, setWanted] = useState(false);
  const [session, setSession] = useState<GlyphSessionApi | null>(null);
  const loginRequested = useRef<GlyphLoginMode | null>(null);
  const [signedOut, setSignedOut] = useState(false);
  const signedOutRef = useRef(false);
  const intent = useRef(0);
  const logoutTask = useRef<Promise<void> | null>(null);
  const [logoutPending, setLogoutPending] = useState(false);
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const publishSession = useCallback((next: GlyphSessionApi) => {
    if (!signedOutRef.current) setSession(next);
  }, []);

  useEffect(() => {
    if (signedOutRef.current || !session?.ready) return;
    if (session.authenticated) {
      loginRequested.current = null;
      remember(true);
    } else if (loginRequested.current) {
      const mode = loginRequested.current;
      loginRequested.current = null;
      if (!session.walletResumeBlocked) login(session, mode);
    } else if (!oauthReturn()) remember(false);
  }, [session]);

  const signIn = useCallback(
    (mode: GlyphLoginMode = "oauth") => {
      const ticket = ++intent.current;
      const current = signedOutRef.current ? null : session;
      const start = () => {
        if (intent.current !== ticket) return;
        if (signedOutRef.current) setSessionEpoch((epoch) => epoch + 1);
        signedOutRef.current = false;
        setSignedOut(false);
        loginRequested.current = mode;
        // Persist before an OAuth redirect, not after it.
        remember(true);
        setWanted(true);
        if (current?.ready) {
          loginRequested.current = null;
          login(current, mode);
        }
      };
      // A slow old logout must not erase a newly requested login.
      if (logoutTask.current) void logoutTask.current.then(start);
      else start();
    },
    [session]
  );

  const signOut = useCallback(() => {
    ++intent.current;
    signedOutRef.current = true;
    setSignedOut(true);
    setWanted(false);
    loginRequested.current = null;
    remember(false);
    setSession(null);
    // Keep Privy mounted until its teardown settles, but ignore its stale publications.
    setLogoutPending(true);
    if (!logoutTask.current) {
      const task = Promise.resolve()
        .then(() => session?.logout())
        .catch(() => undefined)
        .finally(() => {
          if (logoutTask.current !== task) return;
          logoutTask.current = null;
          setLogoutPending(false);
        });
      logoutTask.current = task;
    }
  }, [session]);
  return {
    session,
    signedOut,
    signIn,
    signOut,
    publishSession,
    sessionEpoch,
    shouldMount: logoutPending || (!signedOut && (wanted || wasRemembered)),
    starting: !signedOut && (wanted || wasRemembered),
  };
}
